import { useEffect, useMemo, useRef, useState } from "react";
import { baueAnschreiben } from "../../shared/anschreiben.js";
import type { AngebotErgebnis } from "../../shared/calc.js";
import { formatCent, formatProzent, parseTonnenToKg } from "../../shared/money.js";
import type { PositionDto } from "../../shared/types.js";
import { api } from "../api.js";
import { useStammdaten } from "../StammdatenContext.js";

/**
 * Rechner für die Preisauskunft am Telefon oder per Mail.
 *
 * Kein Kunde, kein Speichern, keine Angebotsnummer. Vergleicht mehrere
 * Artikel bei derselben Menge — das ist die Frage, die wirklich gestellt
 * wird: „Was kostet 550er? Und 1050er?"
 *
 * Gerechnet wird über POST /api/angebote/vorschau, also serverseitig mit
 * frischen Preisen aus der Datenbank. Ein Schnappschuss im Browser würde
 * veralten, sobald ein Admin die Preisliste ändert — und dann nennt der
 * Vertrieb am Telefon einen anderen Preis, als das Angebot später ausweist.
 */

const MAX_ZEILEN = 4;

interface Zeile {
  product_id: string;
  packaging_id: string;
  surcharge_ids: string[];
  rabatt_bp: number;
}

const leereZeile = (): Zeile => ({
  product_id: "",
  packaging_id: "",
  surcharge_ids: [],
  rabatt_bp: 0,
});

export function Schnellkalkulation({
  beiUebernahme,
}: {
  beiUebernahme: (positionen: PositionDto[]) => void;
}) {
  const { stamm, fehler: stammFehler, geladenAm, neuLaden } = useStammdaten();

  const [zeilen, setZeilen] = useState<Zeile[]>([leereZeile()]);
  const [mengeText, setMengeText] = useState("25");
  const [lieferbedingung, setLieferbedingung] =
    useState<"ab_werk" | "frachtfrei_empfaenger">("frachtfrei_empfaenger");
  const [frachtzone, setFrachtzone] = useState("");
  const [jeTonne, setJeTonne] = useState(false);
  // Steht im Anschreiben und gehoert deshalb in die Maske. VDM-Vorgabe: 14 Tage.
  const [zahlungszielText, setZahlungszielText] = useState("14");

  const [ergebnis, setErgebnis] = useState<AngebotErgebnis | null>(null);
  const [rechenFehler, setRechenFehler] = useState<string | null>(null);
  const [rechnet, setRechnet] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  const mengeKg = parseTonnenToKg(mengeText);

  /*
   * useMemo, nicht einfach filter(): Ein frisch gefiltertes Array hat bei
   * jedem Render eine neue Referenz. Der Abrufeffekt hing daran, lief also
   * bei jedem Render erneut — und weil der Abruf `rechnet` auf true und
   * wieder auf false setzt, löste er zwei weitere Renders aus. Ergebnis war
   * eine sich selbst tragende Schleife mit einer Anfrage pro Sekunde,
   * dauerhaft, ohne jede Eingabe.
   */
  const gefuellt = useMemo(() => zeilen.filter((z) => z.product_id), [zeilen]);

  const positionen = useMemo<PositionDto[]>(() => {
    if (!stamm || Number.isNaN(mengeKg) || mengeKg <= 0) return [];
    return gefuellt.map((z) => ({
      product_id: z.product_id,
      packaging_id: z.packaging_id || null,
      bezeichnung: "",
      menge_kg: mengeKg,
      ca_menge: true,
      surcharge_ids: z.surcharge_ids,
      rabatt_bp: z.rabatt_bp,
    }));
  }, [stamm, gefuellt, mengeKg]);

  /** Entprellt, damit nicht jeder Tastendruck einen Request auslöst. */
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    const pos = positionen;
    if (!pos.length) {
      setErgebnis(null);
      setRechenFehler(null);
      return;
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setRechnet(true);
      api
        .post<AngebotErgebnis>("/angebote/vorschau", {
          lieferbedingung,
          freight_zone_id: lieferbedingung === "ab_werk" ? frachtzone || null : null,
          positionen: pos,
        })
        .then((e) => {
          setErgebnis(e);
          setRechenFehler(null);
        })
        .catch((e) => {
          setErgebnis(null);
          setRechenFehler(e instanceof Error ? e.message : "Berechnung fehlgeschlagen.");
        })
        .finally(() => setRechnet(false));
    }, 300);
    return () => window.clearTimeout(timer.current);
  }, [positionen, lieferbedingung, frachtzone]);

  function aendere(i: number, teil: Partial<Zeile>) {
    setZeilen((alt) => alt.map((z, j) => (i === j ? { ...z, ...teil } : z)));
  }

  /** Reine Anzeigeumrechnung — nie ein zweiter Rechenweg. */
  const anzeige = (centJe100kg: number) =>
    formatCent(jeTonne ? centJe100kg * 10 : centJe100kg);
  const einheit = jeTonne ? "€/t" : "€/100 kg";

  /**
   * Das Anschreiben der Muehle — Wortlaut und Reihenfolge kommen aus
   * `shared/anschreiben.ts`, damit Schnellkalkulation und Angebot denselben
   * Brief erzeugen.
   *
   * Was die Schnellkalkulation nicht weiss, faellt weg statt erfunden zu
   * werden: Ohne Kunden gibt es keinen Ort ("Frachtfrei Ihrem Werk" ohne
   * Zusatz), ohne Kontrakt keine Laufzeitzeile.
   */
  function baueText(): string {
    if (!ergebnis || !stamm) return "";
    const e = stamm.einstellungen ?? {};

    return baueAnschreiben({
      verbindlichkeit: "freibleibend",
      positionen: ergebnis.positionen.map((p, i) => {
        const roh = gefuellt[i];
        return {
          bezeichnung:
            stamm.produkte.find((x) => x.id === roh?.product_id)?.bezeichnung ?? "—",
          verpackung:
            stamm.verpackungen.find((v) => v.id === roh?.packaging_id)?.bezeichnung ?? null,
          menge_kg: mengeKg,
          preis_cent: p.endpreis_cent,
        };
      }),
      lieferbedingung,
      werk_ort: e.firma_plz_ort?.replace(/^\d{5}\s*/, "") || null,
      zahlungsziel_tage: Number(zahlungszielText) || 14,
      fusstext:
        e.angebot_fusstext ??
        "Alle Preise verstehen sich je 100 kg netto, zzgl. gesetzlicher Umsatzsteuer.",
      agb_hinweis: e.agb_hinweis ?? null,
      // Eine Preisauskunft per Mail ist ein Geschaeftsbrief. Bei einer
      // GmbH & Co. KG verlangt § 125a HGB zusaetzlich die Angaben zur
      // Komplementaerin — dieselbe Regel, die das Angebots-PDF durchsetzt.
      absender: [
        e.firma_name,
        [e.firma_strasse, e.firma_plz_ort].filter(Boolean).join(", "),
        e.firma_registergericht && e.firma_hra ? `${e.firma_registergericht} ${e.firma_hra}` : "",
        e.komplementaer_firma
          ? `Komplementärin: ${e.komplementaer_firma}${e.komplementaer_sitz ? `, ${e.komplementaer_sitz}` : ""}` +
            (e.komplementaer_hrb ? ` (${e.komplementaer_hrb})` : "")
          : "",
        e.geschaeftsfuehrer ? `Geschäftsführer: ${e.geschaeftsfuehrer}` : "",
        [e.firma_telefon, e.firma_email].filter(Boolean).join(" · "),
      ].filter(Boolean),
    });
  }

  const pflichtFehlt = stamm
    ? (["komplementaer_firma", "komplementaer_hrb", "geschaeftsfuehrer"] as const).filter(
        (k) => !(stamm.einstellungen ?? {})[k],
      )
    : [];

  async function melden(anlass: "kopiert" | "uebernommen") {
    if (!ergebnis) return;
    await api
      .post("/angebote/schnellauskunft", {
        anlass,
        positionen: ergebnis.positionen.map((p, i) => ({
          bezeichnung: stamm?.produkte.find((x) => x.id === gefuellt[i]?.product_id)?.bezeichnung,
          menge_kg: mengeKg,
          endpreis_cent: p.endpreis_cent,
          rabatt_bp: gefuellt[i]?.rabatt_bp ?? 0,
        })),
      })
      .catch(() => {});
  }

  async function kopieren() {
    await navigator.clipboard.writeText(baueText()).catch(() => {});
    setKopiert(true);
    window.setTimeout(() => setKopiert(false), 2500);
    void melden("kopiert");
  }

  function uebernehmen() {
    void melden("uebernommen");
    beiUebernahme(positionen);
  }

  if (stammFehler && !stamm)
    return (
      <div className="meldung fehler" role="alert">
        {stammFehler}
      </div>
    );
  if (!stamm) return <p className="leer">Stammdaten werden geladen …</p>;

  return (
    <>
      <div className="pos-kopf" style={{ marginBottom: "1rem" }}>
        <h1 style={{ margin: 0 }}>Schnellkalkulation</h1>
        <div className="knopfzeile">
          <span className="hinweistext" style={{ margin: 0 }}>
            Preisstand{" "}
            {geladenAm
              ? new Date(geladenAm).toLocaleTimeString("de-DE", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—"}
          </span>
          <button className="knopf schlicht" onClick={neuLaden}>
            Preise neu laden
          </button>
        </div>
      </div>

      <p className="hinweistext" style={{ marginTop: 0, marginBottom: "1.1rem" }}>
        Für die Auskunft am Telefon oder per Mail. Wird nicht gespeichert und bekommt keine
        Angebotsnummer — gerechnet wird trotzdem serverseitig mit den aktuellen Preisen.
      </p>

      <section className="karte">
        <div className="raster">
          <div className="feld">
            <label htmlFor="sk-menge">Menge (t)</label>
            <input
              id="sk-menge"
              inputMode="decimal"
              value={mengeText}
              onChange={(e) => setMengeText(e.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="sk-lb">Lieferbedingung</label>
            <select
              id="sk-lb"
              value={lieferbedingung}
              onChange={(e) => setLieferbedingung(e.target.value as typeof lieferbedingung)}
            >
              <option value="frachtfrei_empfaenger">frachtfrei Käuferstation</option>
              <option value="ab_werk">ab Werk (Selbstabholung)</option>
            </select>
          </div>
          {lieferbedingung === "ab_werk" && (
            <div className="feld">
              <label htmlFor="sk-fz">Frachtzone</label>
              <select id="sk-fz" value={frachtzone} onChange={(e) => setFrachtzone(e.target.value)}>
                <option value="">— keine —</option>
                {stamm.frachtzonen.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.bezeichnung}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="feld">
            <label htmlFor="sk-ziel">Zahlungsziel (Tage)</label>
            <input
              id="sk-ziel"
              inputMode="numeric"
              value={zahlungszielText}
              onChange={(e) => setZahlungszielText(e.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="sk-einheit">Anzeige</label>
            <select
              id="sk-einheit"
              value={jeTonne ? "t" : "100kg"}
              onChange={(e) => setJeTonne(e.target.value === "t")}
            >
              <option value="100kg">€ je 100 kg</option>
              <option value="t">€ je Tonne</option>
            </select>
          </div>
        </div>
      </section>

      <section className="karte">
        <h2>Artikel vergleichen</h2>
        <div className="tabelle-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Artikel</th>
                <th style={{ minWidth: 150 }}>Verpackung</th>
                <th style={{ width: 90 }}>Rabatt %</th>
                <th className="zahl">Preis {einheit}</th>
                <th className="zahl">Summe netto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z, i) => {
                const e = ergebnis?.positionen[gefuellt.indexOf(z)];
                const produkt = stamm.produkte.find((x) => x.id === z.product_id);
                return (
                  <tr key={i}>
                    <td>
                      <select
                        value={z.product_id}
                        onChange={(ev) => aendere(i, { product_id: ev.target.value })}
                      >
                        <option value="">— bitte wählen —</option>
                        {/*
                          Ein Artikel ohne Listenpreis steht mit dabei, aber
                          erkennbar: Die Rechnung weist ihn ab, und ohne
                          Kennzeichnung wäre nicht zu sehen, warum
                          ausgerechnet dieser nicht geht.
                        */}
                        {stamm.produkte.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.bezeichnung}
                            {Number(p.basispreis_e4) <= 0 ? " — kein Listenpreis" : ""}
                          </option>
                        ))}
                      </select>
                      {produkt && Number(produkt.basispreis_e4) <= 0 && (
                        <p className="meldung fehler" style={{ margin: "0.25rem 0 0" }}>
                          Kein Listenpreis gepflegt — unter Stammdaten nachtragen.
                        </p>
                      )}
                      {produkt?.warengruppe === "pfand" && (
                        <p className="hinweistext" style={{ margin: "0.25rem 0 0" }}>
                          Pfand rechnet je Stück — dafür die Angebotsmaske nutzen.
                        </p>
                      )}
                    </td>
                    <td>
                      <select
                        value={z.packaging_id}
                        onChange={(ev) => aendere(i, { packaging_id: ev.target.value })}
                      >
                        <option value="">—</option>
                        {stamm.verpackungen.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.bezeichnung}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        inputMode="decimal"
                        placeholder="0"
                        value={z.rabatt_bp ? String(z.rabatt_bp / 100).replace(".", ",") : ""}
                        onChange={(ev) => {
                          const n = Number(ev.target.value.replace(",", "."));
                          aendere(i, {
                            rabatt_bp: Number.isFinite(n)
                              ? Math.max(0, Math.min(10_000, Math.round(n * 100)))
                              : 0,
                          });
                        }}
                      />
                    </td>
                    <td className="zahl" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {e ? anzeige(e.endpreis_cent) : "—"}
                    </td>
                    <td className="zahl" style={{ whiteSpace: "nowrap" }}>
                      {e ? `${formatCent(e.positionssumme_cent)} €` : "—"}
                    </td>
                    <td>
                      {zeilen.length > 1 && (
                        <button
                          className="knopf schlicht"
                          onClick={() => setZeilen((a) => a.filter((_, j) => j !== i))}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {zeilen.length < MAX_ZEILEN && (
          <button
            className="knopf sekundaer"
            style={{ marginTop: "0.8rem" }}
            onClick={() => setZeilen((a) => [...a, leereZeile()])}
          >
            + Artikel zum Vergleich
          </button>
        )}

        {rechenFehler && (
          <div className="meldung fehler" style={{ marginTop: "1rem" }} role="alert">
            {rechenFehler}
          </div>
        )}
      </section>

      {ergebnis && (
        <section className="karte">
          <h2>Ergebnis {rechnet && <span className="hinweistext">· wird berechnet …</span>}</h2>
          <div className="rechnung" style={{ maxWidth: 460 }}>
            <div className="rechnung-zeile summe">
              <span className="bez">Summe netto</span>
              <span>{formatCent(ergebnis.summe_netto_cent)} €</span>
            </div>
            {ergebnis.steuern.map((t) => (
              <div className="rechnung-zeile" key={t.ust_bp}>
                <span className="bez">
                  zzgl. {formatProzent(t.ust_bp)} % USt auf {formatCent(t.netto_cent)}
                </span>
                <span>{formatCent(t.ust_cent)} €</span>
              </div>
            ))}
            <div className="rechnung-zeile gesamt">
              <span className="bez">Gesamtbetrag</span>
              <span>{formatCent(ergebnis.summe_brutto_cent)} €</span>
            </div>
          </div>

          {pflichtFehlt.length > 0 && (
            <div className="meldung hinweis" style={{ marginTop: "1rem" }}>
              Im Firmenkopf fehlen Pflichtangaben zur Komplementärin. Eine Preisauskunft per Mail
              ist ein Geschäftsbrief — bitte unter „Stammdaten → Firmenkopf“ nachtragen.
            </div>
          )}

          <div className="knopfzeile" style={{ marginTop: "1.2rem" }}>
            <button className="knopf" onClick={kopieren}>
              {kopiert ? "In der Zwischenablage" : "Anschreiben kopieren"}
            </button>
            <button className="knopf sekundaer" onClick={uebernehmen}>
              Als Angebot übernehmen
            </button>
          </div>

          <details style={{ marginTop: "1rem" }}>
            <summary className="hinweistext" style={{ cursor: "pointer" }}>
              Anschreiben ansehen
            </summary>
            <pre className="textvorschau">{baueText()}</pre>
          </details>
        </section>
      )}
    </>
  );
}
